import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  MessageCircle, Users, Lock, Crown, ChevronRight, 
  Shield, Sparkles, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const AGE_GROUPS = [
  { 
    id: "40s", 
    label: "40대 채팅방", 
    desc: "활기차고 열정적인 40대 회원들의 소통 공간",
    color: "bg-emerald-500",
    lightColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    activeColor: "bg-emerald-500 text-white",
    minYear: 1977,
    maxYear: 1986,
  },
  { 
    id: "50s", 
    label: "50대 채팅방", 
    desc: "성숙하고 여유로운 50대 회원들의 소통 공간",
    color: "bg-blue-500",
    lightColor: "bg-blue-50 text-blue-700 border-blue-200",
    activeColor: "bg-blue-500 text-white",
    minYear: 1967,
    maxYear: 1976,
  },
  { 
    id: "60s", 
    label: "60대 채팅방", 
    desc: "따뜻하고 지혜로운 60대 회원들의 소통 공간",
    color: "bg-purple-500",
    lightColor: "bg-purple-50 text-purple-700 border-purple-200",
    activeColor: "bg-purple-500 text-white",
    minYear: 1957,
    maxYear: 1966,
  },
];

const MEMBERSHIP_LEVELS = {
  basic: 0,
  regular: 1,
  premium: 2,
  vip: 3,
};

export default function GroupChatLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [showAgePopup, setShowAgePopup] = useState(false);
  const [agePopupInfo, setAgePopupInfo] = useState(null);

  const membershipType = user?.membership_type || "basic";
  const canAccess = MEMBERSHIP_LEVELS[membershipType] >= MEMBERSHIP_LEVELS["regular"];

  // 내 연령대에 해당하는 채팅방만 필터
  const myAgeGroups = AGE_GROUPS.filter((group) => {
    if (!user?.birth_year) return true;
    return user.birth_year >= group.minYear && user.birth_year <= group.maxYear;
  });
  const displayGroups = myAgeGroups.length > 0 ? myAgeGroups : AGE_GROUPS;

  const handleEnterChat = (ageGroupId) => {
    if (!isAuthenticated) {
      alert("로그인이 필요합니다.");
      return;
    }
    if (!canAccess) {
      setShowAccessPopup(true);
      return;
    }
    // 출생년도 체크
    const group = AGE_GROUPS.find(g => g.id === ageGroupId);
    const birthYear = user?.birth_year;
    if (birthYear && group) {
      if (birthYear < group.minYear || birthYear > group.maxYear) {
        setAgePopupInfo(group);
        setShowAgePopup(true);
        return;
      }
    }
    navigate(`/chat/group/${ageGroupId}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20" data-testid="group-chat-landing">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-2xl font-bold text-stone-800" data-testid="group-chat-title">
            소그룹 채팅방
          </h1>
        </div>
        <p className="text-stone-500 text-sm leading-relaxed mb-6" data-testid="group-chat-description">
          같은 연령대의 회원들과 편안하게 대화를 나눠보세요.<br />
          공통된 경험과 관심사로 더 깊은 공감을 나눌 수 있는 소통 공간입니다.
        </p>

        {/* Membership Notice */}
        <div className="bg-gradient-to-r from-stone-50 to-gold-50 border border-stone-200 rounded-2xl p-5 mb-8 flex items-start gap-4" data-testid="membership-notice">
          <div className="w-10 h-10 bg-stone-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-stone-800" />
          </div>
          <div>
            <p className="font-semibold text-stone-800 text-sm mb-1">
              정회원 이상만 이용 가능합니다
            </p>
            <p className="text-stone-500 text-xs leading-relaxed">
              소그룹 채팅방은 정회원, 프리미엄, VIP 회원에게만 제공되는 서비스입니다.
              멤버십 업그레이드 후 연령대별 채팅방에서 새로운 인연을 만나보세요.
            </p>
            {isAuthenticated && !canAccess && (
              <Button
                onClick={() => navigate("/membership")}
                className="mt-3 bg-stone-700 hover:bg-stone-800 text-white text-xs px-4 py-2 rounded-lg"
                data-testid="upgrade-membership-btn"
              >
                <Crown className="w-3.5 h-3.5 mr-1.5" />
                멤버십 업그레이드
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Left Sidebar - Age Group Categories */}
          <aside className="w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 sticky top-24" data-testid="age-group-sidebar">
              <h2 className="font-bold text-stone-800 mb-4 px-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-stone-700" />
                연령별 채팅방
              </h2>
              <nav className="space-y-2">
                {displayGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => handleEnterChat(group.id)}
                    onMouseEnter={() => setHoveredGroup(group.id)}
                    onMouseLeave={() => setHoveredGroup(null)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl transition-all duration-200 border ${
                      hoveredGroup === group.id
                        ? group.lightColor
                        : "border-transparent hover:bg-stone-50"
                    }`}
                    data-testid={`sidebar-chat-${group.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${group.color}`} />
                        <span className="text-sm font-medium text-stone-700">
                          {group.label}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-400" />
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            <div className="grid grid-cols-1 gap-5">
              {displayGroups.map((group) => (
                <div
                  key={group.id}
                  className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleEnterChat(group.id)}
                  data-testid={`chat-room-card-${group.id}`}
                >
                  <div className={`h-1.5 ${group.color}`} />
                  <div className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 ${group.color} rounded-2xl flex items-center justify-center`}>
                        <MessageCircle className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-stone-800 text-base">{group.label}</h3>
                        <p className="text-stone-500 text-sm mt-0.5">{group.desc}</p>
                        <p className="text-stone-400 text-xs mt-1">{group.minYear}~{group.maxYear}년생</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {(!isAuthenticated || !canAccess) ? (
                        <div className="flex items-center gap-2 text-stone-400">
                          <Lock className="w-4 h-4" />
                          <span className="text-xs">정회원 이상</span>
                        </div>
                      ) : (
                        <Button
                          className="bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm px-4 py-2 rounded-xl"
                          data-testid={`enter-chat-${group.id}`}
                        >
                          입장하기
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tips Section */}
            <div className="mt-8 bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <h3 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gold-500" />
                채팅방 이용 안내
              </h3>
              <ul className="space-y-2.5 text-sm text-stone-600">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-1.5 flex-shrink-0" />
                  같은 연령대의 회원들과 실시간으로 대화할 수 있습니다.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-1.5 flex-shrink-0" />
                  사진과 동영상을 공유하며 소통할 수 있습니다.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-1.5 flex-shrink-0" />
                  상대방을 존중하는 매너있는 대화를 부탁드립니다.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-1.5 flex-shrink-0" />
                  부적절한 내용은 관리자에 의해 삭제될 수 있습니다.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 정회원 이상 접근 제한 팝업 */}
      {showAccessPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAccessPopup(false)} data-testid="access-popup-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="access-popup">
            <div className="bg-gradient-to-r from-stone-700 to-stone-800 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg">정회원 이상만 참여가 가능합니다</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-stone-600 text-sm leading-relaxed mb-5">
                소그룹 채팅방은 정회원, 프리미엄, VIP 회원에게만<br />
                제공되는 서비스입니다.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowAccessPopup(false)}
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl"
                  data-testid="access-popup-close"
                >
                  닫기
                </Button>
                <Button
                  onClick={() => { setShowAccessPopup(false); navigate("/membership"); }}
                  className="flex-1 bg-stone-700 hover:bg-stone-800 text-white rounded-xl"
                  data-testid="access-popup-upgrade"
                >
                  <Crown className="w-4 h-4 mr-1.5" />
                  멤버십 업그레이드
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 출생년도 제한 팝업 */}
      {showAgePopup && agePopupInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAgePopup(false)} data-testid="age-popup-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="age-popup">
            <div className={`${agePopupInfo.color} p-6 text-center`}>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg">연령대가 맞지 않습니다</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-stone-800 font-semibold text-base mb-2">
                {agePopupInfo.label}
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-2">
                {agePopupInfo.minYear}~{agePopupInfo.maxYear}년생만 참여가 가능합니다.
              </p>
              <p className="text-stone-400 text-xs mb-5">
                회원님의 출생년도: {user?.birth_year}년
              </p>
              <Button
                onClick={() => setShowAgePopup(false)}
                className="w-full bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl"
                data-testid="age-popup-close"
              >
                확인
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
